import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageHeaderFilters, PageContent } from '@/components/ui/page'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Activity, PhoneIncoming, PhoneOutgoing, Loader2 } from 'lucide-react'
import { useState, useMemo } from 'react'
import { useAPIClient } from '@/hooks/useApi'
import { useQuery } from '@tanstack/react-query'
import { CallsTable, type CallsTableRow } from '@/components/CallsTable'
import { TranscriptSheet } from '@/components/TranscriptSheet'
import { useTranscript } from '@/hooks/useTranscript'
import { useCallLogStream } from '@/hooks/useCallLogStream'
import { LiveIndicator } from '@/components/LiveIndicator'
import { useCallLogMutations } from '@/hooks/useCallLogMutations'
import { getActionLabel, getSimpleActionLabel } from '@/utils/actionLabels'
import { CallLogSortField, SortDirection, CallDirection } from '@/api/generated/models'
import { formatRelativeDateTime, formatUTCToLocalDateTime, calculateDuration } from '@/lib/dateUtils'
import { useOrganization } from '@/hooks/useOrganization'
import { useInfiniteCallLogs } from '@/hooks/useInfiniteCallLogs'
import { useInView } from 'react-intersection-observer'
import { UsageAreaCharts } from '@/components/dashboard/UsageAreaCharts'
import { type UsageTimeRange } from '@/hooks/analytics/useUsageDashboard'
import { ErrorBoundary } from 'react-error-boundary'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const Route = createFileRoute('/dashboard')({
  component: Dashboard,
  errorComponent: RouteErrorFallback,
})

// Helper function to format caller name from API data
function formatCallerName(firstName: string | null, lastName: string | null): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  } else if (firstName) {
    return firstName
  } else if (lastName) {
    return lastName
  } else {
    return 'Unbekannt'
  }
}

function Dashboard() {
  const [selectedPhone, setSelectedPhone] = useState('')
  const [timeRange, setTimeRange] = useState<UsageTimeRange>('heute')
  const [directionFilter, setDirectionFilter] = useState<string>('all')
  const [selectedCallLogId, setSelectedCallLogId] = useState<string | null>(null)
  const [isTranscriptSheetOpen, setIsTranscriptSheetOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [callToDelete, setCallToDelete] = useState<{ id: string; datum: string } | null>(null)

  // Connect to SSE stream for real-time call log updates
  const { isConnected } = useCallLogStream()

  // Delete mutation hook
  const { deleteCallLog } = useCallLogMutations()

  // Get API clients
  const { phoneNumbersApi } = useAPIClient()

  // Fetch organization data
  const { data: organization } = useOrganization()

  // Fetch phone numbers using React Query
  const { data: phoneNumbers, isLoading: isLoadingPhones } = useQuery({
    queryKey: ['phoneNumbers'],
    queryFn: async () => {
      const response = await phoneNumbersApi.listPhoneNumbersApiV1PhoneNumbersGet()
      return response.data.items
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
  
  // Fetch recent call logs using infinite scroll
  const {
    data,
    isLoading: isLoadingCalls,
    error: callsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteCallLogs({
    directionFilter: directionFilter as CallDirection | 'all',
    sortField: CallLogSortField.StartedAt,
    sortDirection: SortDirection.Desc,
  })

  // Intersection observer for infinite scroll
  const { ref: observerRef } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  // Flatten all pages into a single array
  const recentCallLogs = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  // Fetch transcript for selected call log
  const { data: transcript, isLoading: isLoadingTranscript, error: transcriptError } = useTranscript(
    selectedCallLogId,
    isTranscriptSheetOpen
  )

  // Handler for row click to open transcript
  const handleRowClick = (row: CallsTableRow) => {
    if (typeof row.id === 'string') {
      setSelectedCallLogId(row.id)
      setIsTranscriptSheetOpen(true)
    }
  }

  // Handle delete click from table
  const handleDeleteClick = (row: CallsTableRow) => {
    if (typeof row.id === 'string') {
      setCallToDelete({ id: row.id, datum: row.datum })
      setIsDeleteDialogOpen(true)
    }
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!callToDelete) return

    try {
      await deleteCallLog.mutateAsync(callToDelete.id)
      setIsDeleteDialogOpen(false)
      setCallToDelete(null)
    } catch (error) {
      console.error('Failed to delete call log:', error)
    }
  }

  const availablePhones = phoneNumbers?.map((p: any) => p.number) ?? []

  // Update selected phone if it's not in the available phones list
  if (availablePhones.length > 0 && !availablePhones.includes(selectedPhone)) {
    setSelectedPhone(availablePhones[0])
  }

  // Process call data for CallsTable component
  const callsTableRows = useMemo((): CallsTableRow[] => {
    // No need to sort here since API returns sorted data
    return recentCallLogs.map((call: any, index) => {
      // Use action_type and action_classname if available (from backend update)
      const action = (call as any).action_type && (call as any).action_classname
        ? { type: (call as any).action_type, classname: (call as any).action_classname }
        : null

      return {
        id: call.id || index,
        name: formatCallerName(call.caller_first_name, call.caller_last_name),
        nummer: call.channel === 'chat' ? '–' : (call.remote_phone_number || 'Unbekannt'), // This is the caller's number
        datum: formatRelativeDateTime(call.started_at),
        datumExact: formatUTCToLocalDateTime(call.started_at),
        dauer: calculateDuration(call.started_at, call.finished_at),
        aktion: action
          ? getActionLabel(action)
          : getSimpleActionLabel(!!call.action_id),
        direction: call.call_direction || 'inbound',
        channel: call.channel
      }
    })
  }, [recentCallLogs])

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle hiddenOnMobile>
              <div className="flex items-center gap-2">
                <PageHeaderHeading>{organization?.name ?? 'Dashboard'}</PageHeaderHeading>
                <LiveIndicator isLive={isConnected} />
              </div>
              <PageHeaderDescription>Übersicht Ihrer Anrufaktivitäten</PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderFilters>
              <Select value={directionFilter} onValueChange={setDirectionFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      <span>Alle Aktivitäten</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={CallDirection.Inbound}>
                    <div className="flex items-center gap-2">
                      <PhoneIncoming className="h-4 w-4" />
                      <span>Eingehend</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={CallDirection.Outbound}>
                    <div className="flex items-center gap-2">
                      <PhoneOutgoing className="h-4 w-4" />
                      <span>Ausgehend</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedPhone} onValueChange={setSelectedPhone} disabled={isLoadingPhones}>
                <SelectTrigger className="w-full sm:w-[250px]">
                  <SelectValue placeholder="Keine Rufnummern" />
                </SelectTrigger>
                <SelectContent>
                  {availablePhones.map((phone: string) => (
                    <SelectItem key={phone} value={phone}>
                      {phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={timeRange} onValueChange={(v) => setTimeRange(v as UsageTimeRange)}>
                <SelectTrigger className="w-full sm:w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="heute">Heute</SelectItem>
                  <SelectItem value="woche">Woche</SelectItem>
                  <SelectItem value="monat">Monat</SelectItem>
                </SelectContent>
              </Select>
            </PageHeaderFilters>
          </PageHeaderContent>
        </PageHeader>

        <PageContent>
          {/* Call Statistics Section */}
          <ErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => {
              const isAccessDenied = error?.response?.status === 403

              return (
                <div className="p-6 border-b">
                  <div className="p-4 border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-950/20">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                      {isAccessDenied
                        ? 'Sie benötigen die "analytics:view" Berechtigung für diese Funktion.'
                        : 'Fehler beim Laden der Anrufstatistiken'}
                    </p>
                    {!isAccessDenied && (
                      <Button
                        onClick={resetErrorBoundary}
                        variant="outline"
                        size="sm"
                        className="mt-2"
                      >
                        Erneut versuchen
                      </Button>
                    )}
                  </div>
                </div>
              )
            }}
            onReset={() => window.location.reload()}
          >
            <UsageAreaCharts timeRange={timeRange} />
          </ErrorBoundary>

          {/* Recent Calls Section */}
          <div className="px-6 pt-2 pb-4 flex-1 min-h-0 flex flex-col">
            <Card className="py-3 gap-2 flex-1 min-h-0 flex flex-col">
              <CardHeader className="px-4 pt-0 pb-0">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Letzte Aktivität
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-0 flex-1 min-h-0 overflow-auto overscroll-contain">
                <CallsTable
                  rows={callsTableRows}
                  isLoading={isLoadingCalls}
                  error={!!callsError}
                  onRowClick={handleRowClick}
                  onDeleteClick={handleDeleteClick}
                  observerRef={observerRef}
                  isLoadingMore={isFetchingNextPage}
                />
              </CardContent>
            </Card>
          </div>
        </PageContent>
      </PageCard>

      <TranscriptSheet
        transcript={transcript ?? null}
        callLogId={selectedCallLogId ?? undefined}
        isLoading={isLoadingTranscript}
        error={transcriptError ?? null}
        open={isTranscriptSheetOpen}
        onOpenChange={setIsTranscriptSheetOpen}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anruf löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diesen Anruf vom {callToDelete?.datum} und das zugehörige Transkript löschen möchten? Tickets, Kontakte und Termine werden nicht gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCallLog.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteCallLog.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteCallLog.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}